import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { AuthShell } from '../components/auth/AuthShell';
import { formatApiError } from '../lib/errors';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const strong = (p) => p.length >= 8 && /[A-Za-z]/.test(p) && /\d/.test(p);

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!strong(password)) return toast.error('Use at least 8 characters with a letter and a number');
    if (password !== confirm) return toast.error('Passwords do not match');
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, { token, new_password: password });
      toast.success('Password updated — sign in with your new password');
      navigate('/login');
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <AuthShell testId="reset-password-page" title="Link missing" description="This reset link is incomplete.">
        <Link to="/forgot-password" className="text-blue-400 hover:text-blue-300 text-sm" data-testid="reset-request-new-link">Request a new link</Link>
      </AuthShell>
    );
  }

  return (
    <AuthShell testId="reset-password-page" title="Choose a new password"
      description="At least 8 characters, including a letter and a number.">
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="new-password" className="text-slate-300">New password</Label>
          <Input id="new-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
            className="bg-slate-800/50 border-slate-700 text-white" data-testid="reset-password-input" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="confirm-password" className="text-slate-300">Confirm new password</Label>
          <Input id="confirm-password" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)}
            className="bg-slate-800/50 border-slate-700 text-white" data-testid="reset-confirm-input" />
        </div>
        <Button type="submit" disabled={loading} className="w-full bg-blue-500 hover:bg-blue-600 text-white h-11"
          data-testid="reset-submit-btn">
          {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Update password
        </Button>
      </form>
    </AuthShell>
  );
}
